import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { images } from '../utils/images';
import { CheckCircle2, Phone, Briefcase, Target, Award, TrendingUp, Users, FileText } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import VideoBackground from '../components/VideoBackground';
import SEO from '../components/SEO';

const EmploymentNdisPage = () => {
  const faqs = [
    {
      question: "What employment supports does the NDIS fund?",
      answer: "The NDIS funds a range of employment supports including job readiness training, resume writing, interview preparation, workplace modifications, on-the-job coaching, and School Leaver Employment Supports (SLES). These are typically funded under Capacity Building — Finding and Keeping a Job."
    },
    {
      question: "Who is eligible for NDIS employment support?",
      answer: "NDIS participants who have employment-related goals in their plan can access funded employment supports. This includes people looking for their first job, those returning to work, or individuals wanting to develop new skills for career advancement."
    },
    {
      question: "What is the difference between SLES and DES?",
      answer: "SLES (School Leaver Employment Supports) is specifically for young people transitioning from school to work, focusing on building foundational skills. DES (Disability Employment Services) provides ongoing job search, placement, and retention support for people already in or actively seeking the workforce."
    },
    {
      question: "Can the NDIS help with workplace modifications?",
      answer: "Yes, the NDIS can fund workplace modifications and assistive technology that help participants perform their job effectively. This might include specialised equipment, ergonomic adjustments, or communication aids."
    },
    {
      question: "How long does employment support last?",
      answer: "The duration varies based on your NDIS plan and individual needs. SLES can last up to three years post-school, while other employment supports may continue as long as they remain in your plan and are helping you achieve your goals."
    },
    {
      question: "Can I get help starting my own business?",
      answer: "The NDIS can fund supports that help you explore self-employment options, including business planning, skill development, and mentoring. Speak with your support coordinator about including micro-enterprise supports in your plan."
    }
  ];

  const scrollToContact = () => { window.location.href = '/contact'; };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Employment On The NDIS" description="NDIS employment support services in Brisbane. Career planning, job readiness training, work experience and on-the-job coaching to help you find meaningful work." />
      <Header />

      <section className="relative pt-32 pb-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ color: '#587740' }} data-testid="employment-ndis-title">
                Employment On The NDIS
              </h1>
              <p className="text-lg md:text-xl leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                Everyone deserves the chance to find meaningful work — work that makes you feel valued, capable, and proud. If you've been told your options are limited, or you've felt like nobody really believes in your potential, that changes here.
              </p>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                In simple terms — we help you figure out what you're good at, what you enjoy, and then we help you get there. Step by step, at your pace, with someone genuinely in your corner.
              </p>
              <p className="text-base leading-relaxed italic mb-4" style={{ color: '#6c935e' }}>
                We see the psychological struggles that go unseen. You are heard and valued and in control.
              </p>
              <p className="text-sm font-semibold mb-8" style={{ color: '#587740' }}>
                Free consultation and personalised action plan — let's explore your employment goals together, at no cost.
              </p>
              <Button onClick={scrollToContact} size="lg" className="text-white hover:opacity-90" style={{ backgroundColor: '#81b956' }} data-testid="employment-hero-cta">
                <Phone className="w-5 h-5 mr-2" /> Contact Us
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src={images.sles4} alt="Employment support training" className="w-full h-[400px] md:h-[500px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center" style={{ color: '#587740' }}>
            Your Pathway to Employment
          </h2>
          <p className="text-lg text-center max-w-4xl mx-auto mb-12" style={{ color: '#5e5e5e' }}>
            The NDIS recognises that having a job isn't just about money — it's about purpose, independence, and feeling like you belong. We help you build the skills, confidence, and real-world connections you need to find work that fits your life. Put simply — we don't just help you get a job, we help you keep one and grow in it.
          </p>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img src={images.sles5} alt="Career coaching session" className="w-full h-[350px] object-cover" />
            </div>
            <div>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                Our employment programs meet you where you are. Whether you're a school leaver taking your first steps, someone returning to work, or looking for a career change — we provide tailored support that actually makes a difference. Unlike providers who hand you a list and wish you luck, we stay with you through every step.
              </p>
              <ul className="space-y-3">
                {["Career assessments and goal planning", "Resume writing and interview coaching", "Work experience and job trials", "On-the-job mentoring and support", "Workplace rights and responsibilities training"].map((item, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: '#81b956' }} />
                    <span className="text-base" style={{ color: '#5e5e5e' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" style={{ color: '#587740' }}>
            Employment Services We Offer
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Target, title: "Career Planning", desc: "Comprehensive assessments to identify your strengths, interests, and career aspirations, helping you set realistic and achievable employment goals." },
              { icon: FileText, title: "Job Readiness Training", desc: "Practical workshops covering resume writing, interview techniques, workplace communication, and professional presentation skills." },
              { icon: Briefcase, title: "Work Experience", desc: "Structured placements with local employers to gain hands-on experience, build confidence, and develop industry-specific skills." },
              { icon: Users, title: "On-the-Job Coaching", desc: "Dedicated mentors who provide support in the workplace, helping you settle in, build relationships with colleagues, and meet job requirements." },
              { icon: Award, title: "Skills Certification", desc: "Access to industry-relevant certifications and training programs that enhance your employability and open new career pathways." },
              { icon: TrendingUp, title: "Career Advancement", desc: "Ongoing support to help you progress in your career, take on new responsibilities, and work towards promotions and new opportunities." }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={i} className="border-none shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#AABC5A' }}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-3" style={{ color: '#587740' }}>{item.title}</h3>
                    <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="employment-faq">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12 text-center" style={{ color: '#587740' }}>
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border rounded-lg px-6 shadow-sm" data-testid={`employment-faq-${i}`}>
                <AccordionTrigger className="text-left hover:no-underline py-6" style={{ color: '#587740' }}>
                  <span className="text-lg font-semibold pr-4">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed pb-6" style={{ color: '#5e5e5e' }}>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-12">
            <Button onClick={scrollToContact} size="lg" className="text-white hover:opacity-90" style={{ backgroundColor: '#81b956' }}>
              <Phone className="w-5 h-5 mr-2" /> Contact Us
            </Button>
          </div>
        </div>
      </section>

      <VideoBackground variant="dark" className="py-20">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Your Career Starts With Someone Who Believes in You</h2>
          <p className="text-lg mb-8 text-white/90">We'll help you discover your strengths, build real skills, and find work that makes you feel proud. Free consultation, free action plan — let's talk about what's possible.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="tel:+611300404229">
            <Button size="lg" className="text-base px-8 py-6" style={{ backgroundColor: '#FFFFFF', color: '#587740' }}>
              <Phone className="w-5 h-5 mr-2" /> Call Us Now
            </Button>
          </a>
          <a href="/get-on-ndis-free">
            <Button size="lg" variant="outline" className="text-base px-8 py-6 border-2 border-white text-white hover:bg-white/10">
              Get on the NDIS Free
            </Button>
          </a>
          </div>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default EmploymentNdisPage;
